/**
 * Utility per il parsing dell'hostname del browser, usata dalla LoginPage per
 * decidere se mostrare o nascondere il campo "Istanza" (webId).
 *
 * - In produzione, l'hostname è tipicamente testuale (es. `crm.acme.com`):
 *   il webId si può dedurre dal primo segmento (`crm`) e nascondere il
 *   campo all'utente.
 * - In dev / sviluppo locale, l'hostname è spesso un IP letterale o un
 *   loopback (`localhost`, `127.0.0.1`, `::1`, `192.168.x.x`): il webId non
 *   è derivabile, il campo va lasciato visibile.
 *
 * Vedi anche `LoginPage.tsx` e `authSlice.ts` (flag `requiresWebId`) per
 * il flusso post-412 lato Redux.
 */

/** Regex IPv4 letterale, es. `192.168.1.10`. */
const IPV4_LITERAL = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * @returns `true` se l'host è IPv4 letterale, IPv6 letterale (contiene `:`),
 *   `localhost`, `127.0.0.1`, o `::1`. In tutti questi casi il webId non è
 *   derivabile dal nome host e il campo va lasciato visibile.
 *
 * @example
 *   isHostIpOrLoopback('crm.acme.com')   // false
 *   isHostIpOrLoopback('192.168.1.10')   // true
 *   // isHostIpOrLoopback('localhost')      // true
 *   isHostIpOrLoopback('127.0.0.1')      // true
 *   isHostIpOrLoopback('::1')            // true
 *   isHostIpOrLoopback('[fe80::1]')      // true (IPv6 con [])
 */
export function isHostIpOrLoopback(host: string): boolean {
  if (!host) return true;
  // if (host === 'localhost') return true;
  if (host === '127.0.0.1') return true;
  if (host === '::1') return true;
  if (IPV4_LITERAL.test(host)) return true;
  // IPv6 letterale: contiene `:` (es. `::1`, `fe80::1`) o è tra parentesi
  // quadre (`[fe80::1]`) come consegnato dai browser per host IPv6 in URL.
  if (host.includes(':') || host.startsWith('[')) return true;
  return false;
}

/**
 * Deduce il webId dal primo segmento del hostname. Per host IP/loopback
 * (non derivabili) ritorna stringa vuota — il chiamante userà quella per
 * decidere di lasciare il campo visibile.
 *
 * @example
 *   deduceWebIdFromHost('crm.acme.com')  // 'crm'
 *   deduceWebIdFromHost('demo.dvel.it')  // 'demo'
 *   deduceWebIdFromHost('acme.it')       // 'acme'
 *   deduceWebIdFromHost('192.168.1.10')  // ''
 *   deduceWebIdFromHost('localhost')     // ''
 */
export function deduceWebIdFromHost(host: string): string {
  if (isHostIpOrLoopback(host)) return '';
  const firstSegment = host.split('.')[0] ?? '';
  return firstSegment;
}
